import React, { useEffect, useMemo, useState } from 'react';
import styles from './DataPreview.module.scss';
import Header from 'component/layout/header/Header';
import Button from 'component/atom/forms/button/Button';
import TableComponent from 'component/molecules/Table/TableComponent';
import { useLocation, useParams } from 'react-router-dom';
import { Agent } from 'hooks/endpoints';
import { exportData } from 'helpers/exportData';
import RightSlider from 'component/hoc/rightSlider/RightSlider';
import DescriptionItem from './description-item/DescriptionItem';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import RoundedTabs from 'component/atom/Tabs/RoundedTabs';

const excludedKeys = ['InternalId', 'DateInsertedOn', 'AgentId', 'Id'];

const getItemsArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const humanize = (k) =>
  k
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();

const buildFieldsFromRow = (row) => {
  if (!row || typeof row !== 'object') return [];

  return Object.keys(row)
    .filter((key) => !excludedKeys.includes(key))
    .map((key) => ({
      fieldName: key,
      headerName: humanize(key),
    }));
};

const getRowKey = (row) => {
  if (!row || typeof row !== 'object') return 'id';
  const candidates = ['id', 'Id', 'ID'];
  return candidates.find((c) => c in row) || 'Id';
};

const DataPreview = () => {
  const { botId: agentId } = useParams();
  const location = useLocation();
  const detailId = location.state?.detailId;
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [sliderType, setSliderType] = useState('descriptions');

  const [data, setData] = useState();
  const [headerData, setHeaderData] = useState({
    tableName: 'Data Preview',
    hasPaging: true,
    hasSearch: true,
    fields: [],
  });

  const [nestedTables, setNestedTables] = useState([]);
  const [activeNestedTab, setActiveNestedTab] = useState(0);
  const [activeNestedData, setActiveNestedData] = useState(null);
  const [activeNestedHeaderData, setActiveNestedHeaderData] = useState({
    tableName: 'Nested Table',
    hasPaging: true,
    hasSearch: true,
    fields: [],
  });

  const items = useMemo(() => getItemsArray(data), [data]);
  const firstRow =
    items.length > 0 && typeof items[0] === 'object' ? items[0] : null;

  useEffect(() => {
    if (!firstRow) return;

    const fields = buildFieldsFromRow(firstRow);

    setHeaderData((prev) => ({
      ...prev,
      fields,
    }));
  }, [firstRow]);

  const rowKey = useMemo(() => getRowKey(firstRow), [firstRow]);

  const nestedItems = useMemo(
    () => getItemsArray(activeNestedData),
    [activeNestedData],
  );

  const nestedFirstRow =
    nestedItems.length > 0 && typeof nestedItems[0] === 'object'
      ? nestedItems[0]
      : null;

  const nestedRowKey = useMemo(
    () => getRowKey(nestedFirstRow),
    [nestedFirstRow],
  );

  useEffect(() => {
    if (!nestedFirstRow) return;

    const fields = buildFieldsFromRow(nestedFirstRow);

    setActiveNestedHeaderData((prev) => ({
      ...prev,
      fields,
    }));
  }, [nestedFirstRow]);

  const handleExport = async () => {
    exportData(
      Agent.exportTableDataToCSV(detailId),
      data?.agentTypeConnection?.endpoint,
    );
  };

  const openDescriptions = () => {
    setSliderType('descriptions');
    setShowDescriptions(true);
  };
  const openAssociatedTables = () => {
    const allNestedTables = data?.nestedTables || [];

    setSliderType('associatedTables');
    setNestedTables(allNestedTables);
    setActiveNestedTab(0);
    setActiveNestedData(null);
    setActiveNestedHeaderData({
      tableName:
        allNestedTables[0]?.title ||
        allNestedTables[0]?.apiName ||
        'Nested Table',
      hasPaging: true,
      hasSearch: true,
      fields: [],
    });
    setShowDescriptions(true);
  };

  const handleNestedTableChange = (index) => {
    const table = nestedTables[index];

    setActiveNestedTab(index);
    setActiveNestedData(null);
    setActiveNestedHeaderData({
      tableName: table?.title || table?.apiName || 'Nested Table',
      hasPaging: true,
      hasSearch: true,
      fields: [],
    });
  };
  const activeNestedTable = nestedTables[activeNestedTab] || null;
  return (
    <div className={styles['data-preview-container']}>
      <Header title='Data Preview' isBack isSub />

      <div className={styles['table-card']}>
        <div className={styles['table-header']}>
          <div className={styles['table-header-wrapper']}>
            <div className={styles['table-title-container']}>
              <span className={styles['title']}>
                {data?.agentTypeConnection?.description || '—'}
              </span>

              <div className={styles['info-container']}>
                {0 !== data?.totalCount && (
                  <div
                    className={`${styles.custom} ${styles.status} ${styles.testSuccess}`}
                  >
                    <div className={`${styles.circle} ${styles.healthy}`}></div>
                    Healthy
                  </div>
                )}

                <div className={styles['url-container']}>
                  <p>{data?.agentTypeConnection?.endpoint || '—'}</p>
                </div>

                <span className={styles['records']}>
                  Record: {data?.totalCount ?? items.length ?? 0}
                </span>

                <span className={styles['records']}>
                  Fields: {headerData?.fields?.length || 0}
                </span>
              </div>
            </div>

            <div className={styles['actions-row']}>
              <Button
                label='Associated Tables'
                onClick={openAssociatedTables}
                variant='primary-rev'
                badge={data?.nestedTables?.length || 0}
              />

              <Button
                label='Column Descriptions'
                onClick={openDescriptions}
                variant='light'
              />

              <Button
                label='Export'
                type='download'
                disabled={!items.length}
                onClick={handleExport}
              />
            </div>
          </div>
        </div>

        {headerData && (
          <TableComponent
            rowKey={rowKey}
            headerData={headerData}
            api={{
              get: {
                url: Agent.getTableData(detailId),
                queryKey: ['agent-table-data', agentId, detailId],
                enabled: Boolean(detailId),
              },
            }}
            getData={(resp) => {
              if (resp) setData(resp);
            }}
          />
        )}
      </div>

      <RightSlider
        action={{
          isOpen: showDescriptions,
          setIsOpen: setShowDescriptions,
        }}
        title={
          sliderType === 'associatedTables'
            ? 'Associated Tables'
            : 'Column Descriptions'
        }
        badge={sliderType === 'associatedTables' ? nestedTables.length : null}
        className={styles['slider']}
        titleClassName={styles['slider-header']}
        fullScreen
      >
        <div className={styles.cont}>
          {sliderType === 'associatedTables' ? (
            <div className={styles['nested-wrapper']}>
              {activeNestedTable ? (
                <RoundedTabs
                  activeTab={activeNestedTab}
                  setActiveTab={handleNestedTableChange}
                  tabs={nestedTables.map((table) => ({
                    label: table.title || table.apiName,
                    content: (
                      <TableComponent
                        rowKey={nestedRowKey}
                        headerData={activeNestedHeaderData}
                        api={{
                          get: {
                            url: Agent.NestedTableData(table.id),
                            queryKey: [
                              'agent-nested-table-data',
                              table.id,
                              agentId,
                            ],
                            enabled: Boolean(
                              table?.id && agentId && showDescriptions,
                            ),
                            params: {
                              agentId,
                            },
                          },
                        }}
                        getData={(resp) => {
                          if (
                            activeNestedTab ===
                            nestedTables.findIndex((t) => t.id === table.id)
                          ) {
                            setActiveNestedData(resp);
                          }
                        }}
                      />
                    ),
                  }))}
                />
              ) : (
                <div className={styles['empty-state']}>
                  No associated tables found.
                </div>
              )}
            </div>
          ) : (
            <DescriptionItem />
          )}
        </div>
      </RightSlider>
    </div>
  );
};

export default DataPreview;
